import { downloadBlobFile, downloadResponseFile, notifyDownloadError } from './fileDownload'

/**
 * Utility functions for Excel export functionality
 */

/**
 * Download a blob as an Excel file
 * @param {Blob} blob - The blob data to download
 * @param {string} filename - The filename for the downloaded file
 */
export const downloadExcelFile = (blob, filename) => {
  downloadBlobFile({ data: blob, fileName: filename })
}

/**
 * Download a blob as a CSV file (legacy function for backward compatibility)
 * @param {Blob} blob - The blob data to download
 * @param {string} filename - The filename for the downloaded file
 */
export const downloadCsvFile = (blob, filename) => {
  downloadExcelFile(blob, filename)
}

/**
 * Handle Excel export response and download
 * @param {Object} response - The API response
 * @param {string} defaultFilename - Default filename if not provided in response
 */
export const handleExcelExport = (response, defaultFilename = 'attendance_export.xlsx') => {
  try {
    const filename = downloadResponseFile(response, {
      fileName: defaultFilename,
      contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      successMessage: 'Attendance export downloaded.',
    })
    
    return { success: true, filename }
  } catch (error) {
    console.error('Error handling Excel export:', error)
    notifyDownloadError('Unable to export attendance. Please try again.')
    return { success: false, error: error.message }
  }
}

/**
 * Handle CSV export response and download (legacy function for backward compatibility)
 * @param {Object} response - The API response
 * @param {string} defaultFilename - Default filename if not provided in response
 */
export const handleCsvExport = (response, defaultFilename = 'attendance_export.xlsx') => {
  return handleExcelExport(response, defaultFilename)
}

/**
 * Format date for CSV export
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateForCsv = (date) => {
  if (!date) return 'N/A'
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    return 'N/A'
  }
}

/**
 * Format time for CSV export
 * @param {string} time - The time string to format
 * @returns {string} Formatted time string
 */
export const formatTimeForCsv = (time) => {
  if (!time) return 'N/A'
  
  try {
    // If time is in HH:mm:ss format, convert to 12-hour format
    if (time.includes(':')) {
      const [hours, minutes] = time.split(':')
      const hour = parseInt(hours, 10)
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 || 12
      return `${displayHour}:${minutes} ${ampm}`
    }
    
    return time
  } catch (error) {
    return time || 'N/A'
  }
}

/**
 * Sanitize string for CSV export (escape commas and quotes)
 * @param {string} str - The string to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeForCsv = (str) => {
  if (!str) return 'N/A'
  
  // Convert to string and escape quotes
  const stringValue = String(str)
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  
  return stringValue
}

/**
 * Generate filename for attendance export
 * @param {string} exportType - Type of export (monthly, date_range)
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} prefix - Filename prefix
 * @returns {string} Generated filename
 */
export const generateExportFilename = (exportType, startDate, endDate, prefix = 'attendance_records') => {
  const formatDate = (date) => {
    return date.toISOString().split('T')[0].replace(/-/g, '_')
  }
  
  if (exportType === 'monthly') {
    const month = startDate.getMonth() + 1
    const year = startDate.getFullYear()
    return `${prefix}_${year}_${month.toString().padStart(2, '0')}.xlsx`
  } else {
    return `${prefix}_${formatDate(startDate)}_to_${formatDate(endDate)}.xlsx`
  }
}
